import { useState, useEffect } from 'react';
import { X, Save, Users, CheckSquare, Square } from 'lucide-react';
import { colors } from '../../styles/colors';
import ModalPortal from '../ModalPortal';

/**
 * Modal pour gérer les utilisateurs autorisés d'une connexion bancaire
 *
 * @param {Object} props
 * @param {boolean} props.show - Afficher la modale
 * @param {Function} props.onClose - Fermer la modale
 * @param {Function} props.onSave - Callback (connectionId, userIds) => Promise
 * @param {Array} props.selectedUserIds - IDs des utilisateurs actuellement autorisés
 * @param {Array} props.usersList - Liste complète des utilisateurs { id, nom, prenom, email, role }
 * @param {Object} props.connection - Connexion bancaire à modifier
 */
function ManageUsersModal({
  show,
  onClose,
  onSave,
  selectedUserIds = [],
  usersList = [],
  connection
}) {
  const [selected, setSelected] = useState(new Set(selectedUserIds));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setSelected(new Set(selectedUserIds));
    }
  }, [show, selectedUserIds]);

  if (!show || !connection) return null;

  // Trier les utilisateurs par nom
  const sortedUsers = [...usersList]
    .filter(u => u.actif !== false)
    .sort((a, b) => {
      const nomA = `${a.nom || ''} ${a.prenom || ''}`.toLowerCase();
      const nomB = `${b.nom || ''} ${b.prenom || ''}`.toLowerCase();
      return nomA.localeCompare(nomB);
    });

  const allSelected = sortedUsers.length > 0 && selected.size === sortedUsers.length;

  const toggleUser = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sortedUsers.map(u => u.id)));
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSave(connection.id, Array.from(selected));
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin':
        return { bg: colors.primary.gold + '20', text: colors.primary.gold };
      case 'consultant':
        return { bg: colors.primary.teal + '20', text: colors.primary.teal };
      default:
        return { bg: colors.secondary.lightGray, text: colors.secondary.mediumGray };
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Admin';
      case 'consultant': return 'Consultant';
      case 'visiteur': return 'Visiteur';
      default: return role;
    }
  };

  return (
    <ModalPortal>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: colors.text.white,
          borderRadius: '0.5rem',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
        }}>
          {/* Header */}
          <div style={{
            padding: '1.25rem 1.5rem',
            borderBottom: `1px solid ${colors.border.light}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '0.5rem',
                backgroundColor: colors.primary.teal + '15',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Users size={20} color={colors.primary.teal} />
              </div>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: colors.text.primary
                }}>
                  Gérer les utilisateurs
                </h3>
                <p style={{
                  margin: '0.25rem 0 0 0',
                  fontSize: '0.8125rem',
                  color: colors.text.secondary
                }}>
                  {connection.bank_name}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                color: colors.text.secondary
              }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Description */}
          <div style={{
            padding: '1rem 1.5rem',
            backgroundColor: colors.background.lightGray,
            borderBottom: `1px solid ${colors.border.light}`,
            fontSize: '0.8125rem',
            color: colors.text.secondary
          }}>
            Sélectionnez les utilisateurs autorisés à voir et gérer cette connexion bancaire.
            Les super admins ont toujours accès à toutes les connexions.
          </div>

          {/* Toggle all */}
          <div style={{
            padding: '0.75rem 1.5rem',
            borderBottom: `1px solid ${colors.border.light}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: '0.8125rem', color: colors.text.secondary }}>
              {selected.size} utilisateur{selected.size > 1 ? 's' : ''} sélectionné{selected.size > 1 ? 's' : ''}
            </span>
            <button
              onClick={toggleAll}
              style={{
                background: 'none',
                border: 'none',
                color: colors.primary.teal,
                cursor: 'pointer',
                fontSize: '0.8125rem',
                fontWeight: '500'
              }}
            >
              {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
          </div>

          {/* Users list */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '0.5rem 0'
          }}>
            {sortedUsers.length === 0 ? (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                color: colors.text.secondary,
                fontSize: '0.875rem'
              }}>
                Aucun utilisateur disponible
              </div>
            ) : (
              sortedUsers.map(user => {
                const isSelected = selected.has(user.id);
                const roleStyle = getRoleBadgeColor(user.role);

                return (
                  <div
                    key={user.id}
                    onClick={() => toggleUser(user.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.75rem 1.5rem',
                      gap: '0.75rem',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? colors.primary.teal + '08' : 'transparent',
                      transition: 'background-color 150ms'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = colors.background.lightGray;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isSelected ? colors.primary.teal + '08' : 'transparent';
                    }}
                  >
                    {isSelected ? (
                      <CheckSquare size={20} color={colors.primary.teal} />
                    ) : (
                      <Square size={20} color={colors.secondary.mediumGray} />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: colors.text.primary
                      }}>
                        {user.prenom} {user.nom}
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: colors.text.secondary
                      }}>
                        {user.email}
                      </div>
                    </div>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontSize: '0.6875rem',
                      fontWeight: '500',
                      backgroundColor: roleStyle.bg,
                      color: roleStyle.text
                    }}>
                      {getRoleLabel(user.role)}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '1rem 1.5rem',
            borderTop: `1px solid ${colors.border.light}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem'
          }}>
            <button
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: '0.625rem 1.25rem',
                backgroundColor: 'transparent',
                border: `1px solid ${colors.border.medium}`,
                borderRadius: '0.375rem',
                color: colors.text.secondary,
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                padding: '0.625rem 1.25rem',
                backgroundColor: colors.primary.teal,
                border: 'none',
                borderRadius: '0.375rem',
                color: colors.text.white,
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: submitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                opacity: submitting ? 0.7 : 1
              }}
            >
              <Save size={16} />
              {submitting ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

export default ManageUsersModal;
